/**
 * ReportsPage.jsx
 * CCH Care — Phase 5c
 * Last Modified: 2026-05-14
 *
 * Six tabbed reports with date range picker and recharts visualizations.
 * Role-gated: Staff Activity tab requires Supervisor/Admin.
 *
 * Changes (2026-05-14) — CNA self-report:
 * - Branch on role at the top of the default export. Roles in
 *   VIEW_ALL_ROLES (Care Supervisor / Admin / Physician / Nurse) get
 *   the existing six-tab supervisor interface. CNAs get MyActivityView,
 *   a single-page personal activity dashboard scoped to their own
 *   signatures via the backend's reports.view_own permission and
 *   action=my_activity. Each component owns its own hooks, so the
 *   early-return is safe re: React's rules-of-hooks (role doesn't
 *   change mid-session).
 * - MyActivityView reuses StatCard / ChartCard / SectionTitle /
 *   dateInputStyle from the supervisor side to keep visual consistency.
 *
 * Not in v1: late documentation incidents. The schema for the
 * late_documentation flag (column on adl_signatures vs adl_records vs
 * status enum value) hasn't been confirmed against the deployed
 * migrations yet. Adding a "Late documentation" card to MyActivityView
 * is queued for the next iteration once the schema is nailed down.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  LineChart, Line,
} from 'recharts';
import { COLORS } from '../../config/constants';
import client from '../../api/client';
import { useAuth } from '../auth/useAuth';

function decodeHtml(str) {
  if (!str) return str;
  return str
    .replace(/&#039;/g, "'")
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"');
}

// Roles with reports.view (facility-wide). Mirrors backend
// CARE_ROLE_PERMISSIONS. CNAs land on MyActivityView instead.
const VIEW_ALL_ROLES = ['Care Supervisor', 'Care Admin', 'Physician', 'Nurse'];

const TABS = [
  { key: 'shift_summary',        label: '📊 Summary',      roles: null },
  { key: 'adl_compliance',       label: '📋 ADL',          roles: null },
  { key: 'mar_compliance',       label: '💊 MAR',          roles: null },
  { key: 'resident_history',     label: '👥 Residents',    roles: null },
  { key: 'controlled_substances',label: '🔐 Controlled',   roles: null },
  { key: 'staff_activity',       label: '👤 Staff',        roles: ['Care Supervisor','Care Admin'] },
];

// Format a Date as YYYY-MM-DD in local time (avoids UTC off-by-one)
const fmtDate = d => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;

// Default to last 7 days
function defaultDates() {
  const to   = new Date();
  const from = new Date();
  from.setDate(from.getDate() - 6);
  return { from: fmtDate(from), to: fmtDate(to) };
}

export default function ReportsPage() {
  const { memberships } = useAuth();
  const role = memberships?.[0]?.role ?? '';

  // CNA-mode: personal activity. Role-based check mirrors the backend's
  // requireAnyCarePermission gate on action=my_activity (CNA gets there
  // via reports.view_own; supervisors get there via reports.view but
  // are routed to the full tabbed view here instead).
  if (!VIEW_ALL_ROLES.includes(role)) {
    return <MyActivityView />;
  }

  return <SupervisorReportsView role={role} />;
}

// ── Supervisor-mode (existing) ────────────────────────────────

function SupervisorReportsView({ role }) {
  const today = fmtDate(new Date());

  const [activeTab, setActiveTab]   = useState('shift_summary');
  const [dates, setDates]           = useState(defaultDates);
  const [data, setData]             = useState({});
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState('');

  const visibleTabs = TABS.filter(t => !t.roles || t.roles.includes(role));

  const loadReport = useCallback(async (tab, from, to) => {
    setLoading(true);
    setError('');
    try {
      const res = await client.get('/reports/reports.php', {
        params: { action: tab, date_from: from, date_to: to },
      });
      if (res.data.success) {
        setData(prev => ({ ...prev, [tab]: res.data }));
      }
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadReport(activeTab, dates.from, dates.to);
  }, [activeTab, dates, loadReport]);

  function handleDateChange(field, value) {
    if (!value) return;
    // Clamp future dates to today
    const clamped = value > today ? today : value;
    setDates(prev => {
      const next = { ...prev, [field]: clamped };
      // Keep from <= to — auto-adjust the other side if user crosses it
      if (next.from > next.to) {
        if (field === 'from') next.to = next.from;
        else next.from = next.to;
      }
      return next;
    });
  }

  const tabData = data[activeTab];

  return (
    <div style={{ paddingBottom: 80 }}>

      {/* ── Header ── */}
      <div style={{
        padding: '14px 16px 0',
        background: COLORS.surface,
        borderBottom: `1px solid ${COLORS.border}`,
        position: 'sticky', top: 0, zIndex: 10,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div style={{ fontSize: 17, fontWeight: 700, color: COLORS.text }}>Reports</div>

          {/* Date range picker */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input
              type="date" value={dates.from}
              max={today}
              onChange={e => handleDateChange('from', e.target.value)}
              style={dateInputStyle}
            />
            <span style={{ fontSize: 12, color: COLORS.textMuted }}>→</span>
            <input
              type="date" value={dates.to}
              max={today}
              onChange={e => handleDateChange('to', e.target.value)}
              style={dateInputStyle}
            />
          </div>
        </div>

        {/* Quick date presets */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
          {[
            { label: 'Today',   days: 0 },
            { label: '7 days',  days: 6 },
            { label: '30 days', days: 29 },
          ].map(preset => (
            <button key={preset.label} onClick={() => {
              const to   = new Date();
              const from = new Date();
              from.setDate(from.getDate() - preset.days);
              setDates({ from: fmtDate(from), to: fmtDate(to) });
            }} style={{
              padding: '4px 12px', borderRadius: 20, fontSize: 11, fontWeight: 600,
              background: 'none', color: COLORS.textMuted,
              border: `1px solid ${COLORS.border}`, cursor: 'pointer',
            }}>
              {preset.label}
            </button>
          ))}
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', overflowX: 'auto', gap: 0 }}>
          {visibleTabs.map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{
              padding: '10px 14px', background: 'none', border: 'none',
              borderBottom: `3px solid ${activeTab === tab.key ? COLORS.primary : 'transparent'}`,
              color: activeTab === tab.key ? COLORS.primary : COLORS.textMuted,
              fontSize: 12, fontWeight: activeTab === tab.key ? 700 : 500,
              cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
            }}>
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Content ── */}
      <div style={{ padding: '16px 16px' }}>
        {loading && (
          <div style={{ textAlign: 'center', padding: 40, color: COLORS.textMuted }}>
            <div style={{ fontSize: 24, marginBottom: 8 }}>⏳</div>
            Loading report&hellip;
          </div>
        )}

        {error && !loading && (
          <div style={{
            background: COLORS.dangerLight, border: `1px solid ${COLORS.danger}`,
            borderRadius: 10, padding: '12px 16px', color: COLORS.danger, fontSize: 13,
          }}>
            ⚠️ {error}
          </div>
        )}

        {!loading && !error && tabData && (
          <>
            {activeTab === 'shift_summary'         && <ShiftSummaryTab data={tabData} />}
            {activeTab === 'adl_compliance'        && <ADLComplianceTab data={tabData} />}
            {activeTab === 'mar_compliance'        && <MARComplianceTab data={tabData} />}
            {activeTab === 'resident_history'      && <ResidentHistoryTab data={tabData} />}
            {activeTab === 'controlled_substances' && <ControlledSubstancesTab data={tabData} />}
            {activeTab === 'staff_activity'        && <StaffActivityTab data={tabData} />}
          </>
        )}
      </div>
    </div>
  );
}

// ── CNA-mode: My Activity ─────────────────────────────────────
// Single-page personal activity dashboard. Calls /reports/reports.php
// ?action=my_activity, which is gated on reports.view OR reports.view_own
// and scopes by the caller's membership_id. Layout: stat row, daily
// trend, sections breakdown, recent activity list.

function MyActivityView() {
  const today = fmtDate(new Date());

  const [dates, setDates]     = useState(defaultDates);
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  const loadActivity = useCallback(async (from, to) => {
    setLoading(true);
    setError('');
    try {
      const res = await client.get('/reports/reports.php', {
        params: { action: 'my_activity', date_from: from, date_to: to },
      });
      if (res.data.success) {
        setData(res.data);
      }
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Failed to load activity');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadActivity(dates.from, dates.to);
  }, [dates, loadActivity]);

  function handleDateChange(field, value) {
    if (!value) return;
    const clamped = value > today ? today : value;
    setDates(prev => {
      const next = { ...prev, [field]: clamped };
      if (next.from > next.to) {
        if (field === 'from') next.to = next.from;
        else next.from = next.to;
      }
      return next;
    });
  }

  return (
    <div style={{ paddingBottom: 80 }}>

      {/* ── Header ── */}
      <div style={{
        padding: '14px 16px',
        background: COLORS.surface,
        borderBottom: `1px solid ${COLORS.border}`,
        position: 'sticky', top: 0, zIndex: 10,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 700, color: COLORS.text }}>My Activity</div>
            <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 2 }}>
              Your documentation over time
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input
              type="date" value={dates.from}
              max={today}
              onChange={e => handleDateChange('from', e.target.value)}
              style={dateInputStyle}
            />
            <span style={{ fontSize: 12, color: COLORS.textMuted }}>→</span>
            <input
              type="date" value={dates.to}
              max={today}
              onChange={e => handleDateChange('to', e.target.value)}
              style={dateInputStyle}
            />
          </div>
        </div>

        <div style={{ display: 'flex', gap: 6 }}>
          {[
            { label: 'Today',   days: 0 },
            { label: '7 days',  days: 6 },
            { label: '30 days', days: 29 },
          ].map(preset => (
            <button key={preset.label} onClick={() => {
              const to   = new Date();
              const from = new Date();
              from.setDate(from.getDate() - preset.days);
              setDates({ from: fmtDate(from), to: fmtDate(to) });
            }} style={{
              padding: '4px 12px', borderRadius: 20, fontSize: 11, fontWeight: 600,
              background: 'none', color: COLORS.textMuted,
              border: `1px solid ${COLORS.border}`, cursor: 'pointer',
            }}>
              {preset.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Content ── */}
      <div style={{ padding: '16px' }}>
        {loading && (
          <div style={{ textAlign: 'center', padding: 40, color: COLORS.textMuted }}>
            <div style={{ fontSize: 24, marginBottom: 8 }}>⏳</div>
            Loading your activity&hellip;
          </div>
        )}

        {error && !loading && (
          <div style={{
            background: COLORS.dangerLight, border: `1px solid ${COLORS.danger}`,
            borderRadius: 10, padding: '12px 16px', color: COLORS.danger, fontSize: 13,
          }}>
            ⚠️ {error}
          </div>
        )}

        {!loading && !error && data && <MyActivityContent data={data} />}
      </div>
    </div>
  );
}

function MyActivityContent({ data }) {
  const stats    = data.stats              || {};
  const trend    = data.daily_trend        || [];
  const sections = data.sections_breakdown || [];
  const recent   = data.recent_activity    || [];

  const noActivity = stats.sections_signed === 0;

  return (
    <>
      {/* Top-line stats */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <StatCard label="Records Documented" value={stats.records_signed   || 0} color={COLORS.primary} />
        <StatCard label="Sections Signed"    value={stats.sections_signed  || 0} color={COLORS.accent} />
        <StatCard label="Residents Cared For" value={stats.residents_worked || 0} />
        <StatCard label="Shifts Worked"      value={stats.shifts_worked    || 0} />
      </div>

      {noActivity && (
        <div style={{
          textAlign: 'center', padding: '32px 16px',
          background: COLORS.surface, borderRadius: 10,
          border: `1px solid ${COLORS.border}`, marginBottom: 16,
        }}>
          <div style={{ fontSize: 28, marginBottom: 8 }}>📋</div>
          <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.text, marginBottom: 4 }}>
            No documentation in this period
          </div>
          <div style={{ fontSize: 12, color: COLORS.textMuted }}>
            Try a wider date range, or check the 30-day preset above.
          </div>
        </div>
      )}

      {/* Daily trend — only if there's more than one day of data */}
      {trend.length > 1 && (
        <ChartCard title="Daily Documentation Trend">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={trend} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={COLORS.border} />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={d => d.slice(5)} />
              <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
              <Tooltip />
              <Line
                type="monotone" dataKey="sections_signed"
                stroke={COLORS.primary} strokeWidth={2}
                name="Sections" dot={{ r: 3 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      )}

      {/* Sections breakdown — only if any sections signed */}
      {!noActivity && (
        <ChartCard title="Sections You Sign Most" height={280}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={sections}
              layout="vertical"
              margin={{ top: 0, right: 30, left: 60, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke={COLORS.border} horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 10 }} allowDecimals={false} />
              <YAxis type="category" dataKey="label" tick={{ fontSize: 11 }} width={70} />
              <Tooltip />
              <Bar dataKey="signed_count" name="Signed" fill={COLORS.primary} radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      )}

      {/* Recent activity */}
      {recent.length > 0 && (
        <>
          <div style={{
            fontSize: 12, fontWeight: 700, color: COLORS.textMuted,
            textTransform: 'uppercase', letterSpacing: '0.05em',
            marginBottom: 8, marginTop: 4,
          }}>
            Recent Activity
          </div>

          <div style={{
            background: COLORS.surface, borderRadius: 10,
            border: `1px solid ${COLORS.border}`, overflow: 'hidden',
          }}>
            {recent.map((item, i) => (
              <div key={item.adl_signature_id} style={{
                padding: '10px 14px',
                borderBottom: i < recent.length - 1 ? `1px solid ${COLORS.border}` : 'none',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.text }}>
                    {item.section_label}
                    <span style={{ fontWeight: 400, color: COLORS.textMuted }}>
                      {' · '}{decodeHtml(item.full_name)}
                    </span>
                  </div>
                  <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 2 }}>
                    {item.shift_name} shift
                  </div>
                </div>
                <div style={{
                  fontSize: 11, color: COLORS.textMuted,
                  textAlign: 'right', flexShrink: 0, whiteSpace: 'nowrap',
                }}>
                  {formatActivityTimestamp(item.signed_at)}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </>
  );
}

// Format "2026-05-14 08:43:12" as "May 14, 8:43 AM" (or "Today, 8:43 AM"
// if today). All times are stored UTC-naive in MySQL TIMESTAMP, browser
// renders in local zone via toLocaleString defaults.
function formatActivityTimestamp(ts) {
  if (!ts) return '';
  const d = new Date(ts.replace(' ', 'T'));
  if (isNaN(d)) return ts;

  const now = new Date();
  const sameDay = d.getFullYear() === now.getFullYear()
               && d.getMonth()    === now.getMonth()
               && d.getDate()     === now.getDate();

  const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  if (sameDay) return `Today, ${time}`;

  const datePart = d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  return `${datePart}, ${time}`;
}

// ── Shared components ─────────────────────────────────────────

function SectionTitle({ title, subtitle }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 15, fontWeight: 700, color: COLORS.text }}>{title}</div>
      {subtitle && <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 2 }}>{subtitle}</div>}
    </div>
  );
}

function StatCard({ label, value, sub, color }) {
  return (
    <div style={{
      background: COLORS.surface, borderRadius: 10, padding: '14px 16px',
      border: `1px solid ${COLORS.border}`, flex: 1, minWidth: 0,
    }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
        {label}
      </div>
      <div style={{ fontSize: 26, fontWeight: 800, color: color || COLORS.text }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 3 }}>{sub}</div>}
    </div>
  );
}

function ChartCard({ title, children, height = 220 }) {
  return (
    <div style={{
      background: COLORS.surface, borderRadius: 10, padding: '14px 16px',
      border: `1px solid ${COLORS.border}`, marginBottom: 16,
    }}>
      {title && <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.text, marginBottom: 12 }}>{title}</div>}
      <div style={{ height }}>{children}</div>
    </div>
  );
}

// ── Tab: Shift Summary ────────────────────────────────────────

function ShiftSummaryTab({ data: d }) {
  const adl    = d.adl    || {};
  const mar    = d.mar    || {};
  const vitals = d.vitals || {};

  const adlPieData = [
    { name: 'Completed',   value: adl.completed    || 0 },
    { name: 'In Progress', value: adl.in_progress  || 0 },
    { name: 'Not Started', value: adl.not_started  || 0 },
  ].filter(x => x.value > 0);

  const marPieData = [
    { name: 'Given',         value: mar.given         || 0 },
    { name: 'Refused',       value: mar.refused        || 0 },
    { name: 'Held',          value: mar.held           || 0 },
    { name: 'Not Available', value: mar.not_available  || 0 },
    { name: 'Pending',       value: mar.pending        || 0 },
  ].filter(x => x.value > 0);

  const PIE_COLORS_ADL = [COLORS.accent, COLORS.warning, COLORS.border];
  const PIE_COLORS_MAR = [COLORS.accent, COLORS.danger, COLORS.warning, COLORS.textMuted, COLORS.border];

  return (
    <div>
      <SectionTitle title="Shift Summary" subtitle={`${d.date_from} → ${d.date_to}`} />

      <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.textMuted, textTransform: 'uppercase', marginBottom: 8 }}>ADL Documentation</div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <StatCard label="Total Records"   value={adl.total_records || 0} />
        <StatCard label="Completed"       value={adl.completed     || 0} color={COLORS.accent} />
        <StatCard label="Completion Rate" value={`${adl.completion_rate || 0}%`} color={adl.completion_rate >= 80 ? COLORS.accent : COLORS.warning} />
      </div>

      {adlPieData.length > 0 && (
        <ChartCard title="ADL Status Breakdown">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={adlPieData} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
                {adlPieData.map((_, i) => <Cell key={i} fill={PIE_COLORS_ADL[i % PIE_COLORS_ADL.length]} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
      )}

      {adl.daily_trend?.length > 1 && (
        <ChartCard title="Daily ADL Completion Trend">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={adl.daily_trend} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={COLORS.border} />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={d => d.slice(5)} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip />
              <Line type="monotone" dataKey="completed" stroke={COLORS.accent}  strokeWidth={2} name="Completed" dot={false} />
              <Line type="monotone" dataKey="total"     stroke={COLORS.primary} strokeWidth={2} name="Total"     dot={false} strokeDasharray="4 2" />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      )}

      <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.textMuted, textTransform: 'uppercase', marginBottom: 8, marginTop: 8 }}>Medication Administration</div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <StatCard label="Total Doses"     value={mar.total_doses    || 0} />
        <StatCard label="Given"           value={mar.given          || 0} color={COLORS.accent} />
        <StatCard label="Compliance Rate" value={`${mar.compliance_rate || 0}%`} color={mar.compliance_rate >= 90 ? COLORS.accent : COLORS.warning} />
      </div>

      {marPieData.length > 0 && (
        <ChartCard title="MAR Status Breakdown">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={marPieData} cx="50%" cy="50%" outerRadius={80} dataKey="value">
                {marPieData.map((_, i) => <Cell key={i} fill={PIE_COLORS_MAR[i % PIE_COLORS_MAR.length]} />)}
              </Pie>
              <Tooltip />
              <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
      )}

      <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.textMuted, textTransform: 'uppercase', marginBottom: 8, marginTop: 8 }}>Vitals</div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <StatCard label="Readings Recorded" value={vitals.total_readings || 0} />
        <StatCard label="Abnormal Findings" value={vitals.abnormal_count || 0} color={vitals.abnormal_count > 0 ? COLORS.warning : COLORS.accent} />
      </div>
    </div>
  );
}

// ── Tab: ADL Compliance ───────────────────────────────────────

function ADLComplianceTab({ data: d }) {
  const sections = d.sections || [];

  return (
    <div>
      <SectionTitle title="ADL Section Compliance" subtitle={`${d.date_from} → ${d.date_to} · ${d.total_records} total records`} />

      <ChartCard title="Completion Rate by Section" height={260}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={sections} layout="vertical" margin={{ top: 0, right: 30, left: 60, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={COLORS.border} horizontal={false} />
            <XAxis type="number" domain={[0, 100]} tickFormatter={v => `${v}%`} tick={{ fontSize: 10 }} />
            <YAxis type="category" dataKey="label" tick={{ fontSize: 11 }} width={70} />
            <Tooltip formatter={v => `${v}%`} />
            <Bar dataKey="completion_rate" name="Completion Rate" radius={[0, 4, 4, 0]}>
              {sections.map((s, i) => (
                <Cell key={i} fill={s.completion_rate >= 90 ? COLORS.accent : s.completion_rate >= 70 ? COLORS.warning : COLORS.danger} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      <div style={{ background: COLORS.surface, borderRadius: 10, border: `1px solid ${COLORS.border}`, overflow: 'hidden' }}>
        <div style={{ padding: '10px 14px', borderBottom: `1px solid ${COLORS.border}`, display: 'grid', gridTemplateColumns: '1fr 60px 60px 60px', gap: 8 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: COLORS.textMuted }}>SECTION</span>
          <span style={{ fontSize: 11, fontWeight: 700, color: COLORS.textMuted, textAlign: 'right' }}>SIGNED</span>
          <span style={{ fontSize: 11, fontWeight: 700, color: COLORS.textMuted, textAlign: 'right' }}>MISSED</span>
          <span style={{ fontSize: 11, fontWeight: 700, color: COLORS.textMuted, textAlign: 'right' }}>RATE</span>
        </div>
        {sections.map((s, i) => (
          <div key={i} style={{
            padding: '10px 14px',
            borderBottom: i < sections.length - 1 ? `1px solid ${COLORS.border}` : 'none',
            display: 'grid', gridTemplateColumns: '1fr 60px 60px 60px', gap: 8, alignItems: 'center',
          }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: COLORS.text }}>{s.label}</span>
            <span style={{ fontSize: 12, color: COLORS.accent, textAlign: 'right', fontWeight: 600 }}>{s.signed}</span>
            <span style={{ fontSize: 12, color: s.missed > 0 ? COLORS.danger : COLORS.textMuted, textAlign: 'right', fontWeight: 600 }}>{s.missed}</span>
            <span style={{
              fontSize: 11, fontWeight: 700, textAlign: 'right',
              color: s.completion_rate >= 90 ? COLORS.accent : s.completion_rate >= 70 ? COLORS.warning : COLORS.danger,
            }}>
              {s.completion_rate}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Tab: MAR Compliance ───────────────────────────────────────

function MARComplianceTab({ data: d }) {
  const medications = d.medications || [];
  const daily       = d.daily_trend || [];
  const prn         = d.prn_summary || {};

  return (
    <div>
      <SectionTitle title="MAR Compliance" subtitle={`${d.date_from} → ${d.date_to}`} />

      <StatCard label="PRN Doses Given" value={prn.total_prn || 0} sub={prn.controlled_prn > 0 ? `${prn.controlled_prn} controlled substance PRNs` : null} />
      <div style={{ marginBottom: 16 }} />

      {daily.length > 1 && (
        <ChartCard title="Daily Compliance Rate">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={daily} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={COLORS.border} />
              <XAxis dataKey="dose_date" tick={{ fontSize: 10 }} tickFormatter={d => d.slice(5)} />
              <YAxis domain={[0, 100]} tickFormatter={v => `${v}%`} tick={{ fontSize: 10 }} />
              <Tooltip formatter={v => `${v}%`} />
              <Line type="monotone" dataKey="rate" stroke={COLORS.primary} strokeWidth={2} name="Compliance %" dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      )}

      <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.textMuted, textTransform: 'uppercase', marginBottom: 8 }}>By Medication</div>
      <div style={{ background: COLORS.surface, borderRadius: 10, border: `1px solid ${COLORS.border}`, overflow: 'hidden' }}>
        {medications.slice(0, 20).map((m, i) => (
          <div key={i} style={{
            padding: '10px 14px',
            borderBottom: i < Math.min(medications.length, 20) - 1 ? `1px solid ${COLORS.border}` : 'none',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: COLORS.text }}>{m.drug_name}</span>
                {m.is_controlled && (
                  <span style={{ fontSize: 9, fontWeight: 700, color: COLORS.danger, background: COLORS.dangerLight, padding: '1px 5px', borderRadius: 4 }}>CS</span>
                )}
              </div>
              <span style={{
                fontSize: 12, fontWeight: 700,
                color: m.compliance_rate >= 90 ? COLORS.accent : m.compliance_rate >= 70 ? COLORS.warning : COLORS.danger,
              }}>
                {m.compliance_rate}%
              </span>
            </div>
            <div style={{ display: 'flex', gap: 12, fontSize: 11, color: COLORS.textMuted }}>
              <span>✓ {m.given} given</span>
              {m.refused > 0 && <span style={{ color: COLORS.danger }}>✕ {m.refused} refused</span>}
              {m.held > 0 && <span style={{ color: COLORS.warning }}>⏸ {m.held} held</span>}
              <span>{m.total_doses} total</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Tab: Resident History ─────────────────────────────────────

function ResidentHistoryTab({ data: d }) {
  const residents = d.residents || [];

  const chartData = residents.slice(0, 10).map(r => ({
    name: decodeHtml(r.last_name),
    rate: r.completion_rate,
    avg:  r.avg_sections_signed,
  }));

  return (
    <div>
      <SectionTitle title="Resident ADL History" subtitle={`${d.date_from} → ${d.date_to}`} />

      {chartData.length > 0 && (
        <ChartCard title="Completion Rate by Resident (Bottom 10)" height={260}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} layout="vertical" margin={{ top: 0, right: 30, left: 70, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={COLORS.border} horizontal={false} />
              <XAxis type="number" domain={[0, 100]} tickFormatter={v => `${v}%`} tick={{ fontSize: 10 }} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={70} />
              <Tooltip formatter={v => `${v}%`} />
              <Bar dataKey="rate" name="Completion Rate" radius={[0, 4, 4, 0]}>
                {chartData.map((r, i) => (
                  <Cell key={i} fill={r.rate >= 90 ? COLORS.accent : r.rate >= 70 ? COLORS.warning : COLORS.danger} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      )}

      <div style={{ background: COLORS.surface, borderRadius: 10, border: `1px solid ${COLORS.border}`, overflow: 'hidden' }}>
        <div style={{ padding: '10px 14px', borderBottom: `1px solid ${COLORS.border}`, display: 'grid', gridTemplateColumns: '1fr 50px 60px 60px', gap: 8 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: COLORS.textMuted }}>RESIDENT</span>
          <span style={{ fontSize: 11, fontWeight: 700, color: COLORS.textMuted, textAlign: 'right' }}>SHIFTS</span>
          <span style={{ fontSize: 11, fontWeight: 700, color: COLORS.textMuted, textAlign: 'right' }}>DONE</span>
          <span style={{ fontSize: 11, fontWeight: 700, color: COLORS.textMuted, textAlign: 'right' }}>RATE</span>
        </div>
        {residents.map((r, i) => (
          <div key={i} style={{
            padding: '10px 14px',
            borderBottom: i < residents.length - 1 ? `1px solid ${COLORS.border}` : 'none',
            display: 'grid', gridTemplateColumns: '1fr 50px 60px 60px', gap: 8, alignItems: 'center',
          }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.text }}>{decodeHtml(r.full_name)}</div>
              <div style={{ fontSize: 11, color: COLORS.textMuted }}>
                Rm {r.room_number}{r.unit_name ? ` · ${r.unit_name}` : ''}
              </div>
            </div>
            <span style={{ fontSize: 12, color: COLORS.textMuted, textAlign: 'right' }}>{r.total_shifts}</span>
            <span style={{ fontSize: 12, color: COLORS.accent, textAlign: 'right', fontWeight: 600 }}>{r.completed_shifts}</span>
            <span style={{
              fontSize: 11, fontWeight: 700, textAlign: 'right',
              color: r.completion_rate >= 90 ? COLORS.accent : r.completion_rate >= 70 ? COLORS.warning : COLORS.danger,
            }}>
              {r.completion_rate}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Tab: Controlled Substances ────────────────────────────────

function ControlledSubstancesTab({ data: d }) {
  const csByDrug      = d.cs_by_drug      || [];
  const countsSummary = d.counts_summary  || {};
  const wasteSummary  = d.waste_summary   || {};
  const discrepancies = d.discrepancies   || [];

  const chartData = csByDrug.map(c => ({
    name:         c.drug_name.length > 15 ? c.drug_name.slice(0, 15) + '…' : c.drug_name,
    administered: c.administered_count,
    wasted:       c.wasted_count,
  }));

  return (
    <div>
      <SectionTitle title="Controlled Substances" subtitle={`${d.date_from} → ${d.date_to}`} />

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <StatCard label="Count Sessions"   value={countsSummary.total_counts     || 0} />
        <StatCard label="With Discrepancy" value={countsSummary.with_discrepancy || 0} color={countsSummary.with_discrepancy > 0 ? COLORS.danger : COLORS.accent} />
        <StatCard label="Waste Records"    value={wasteSummary.total_waste       || 0} />
      </div>

      {chartData.length > 0 && (
        <ChartCard title="Administrations & Waste by Drug" height={Math.max(200, chartData.length * 40)}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} layout="vertical" margin={{ top: 0, right: 30, left: 80, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={COLORS.border} horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 10 }} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={80} />
              <Tooltip />
              <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="administered" name="Administered" fill={COLORS.primary} radius={[0, 4, 4, 0]} />
              <Bar dataKey="wasted"       name="Wasted"       fill={COLORS.warning}  radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      )}

      {discrepancies.length > 0 && (
        <>
          <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.danger, textTransform: 'uppercase', marginBottom: 8 }}>
            ⚠ Discrepancy Reports
          </div>
          <div style={{ background: COLORS.surface, borderRadius: 10, border: `1px solid ${COLORS.danger}44`, overflow: 'hidden' }}>
            {discrepancies.map((disc, i) => (
              <div key={i} style={{
                padding: '10px 14px',
                borderBottom: i < discrepancies.length - 1 ? `1px solid ${COLORS.border}` : 'none',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 2 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: COLORS.text }}>{disc.drug_name}</span>
                  <span style={{
                    fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
                    background: disc.status === 'resolved' ? COLORS.accentLight : COLORS.dangerLight,
                    color: disc.status === 'resolved' ? COLORS.accent : COLORS.danger,
                  }}>
                    {disc.status}
                  </span>
                </div>
                <div style={{ fontSize: 11, color: COLORS.textMuted }}>
                  Expected {disc.expected_count} · Found {disc.actual_count} · Difference {disc.discrepancy_amount}
                </div>
                <div style={{ fontSize: 11, color: COLORS.textMuted }}>
                  Reported by {disc.reported_by_name} · {new Date(disc.reported_at).toLocaleDateString()}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {discrepancies.length === 0 && (
        <div style={{
          textAlign: 'center', padding: '24px 16px',
          background: COLORS.accentLight, borderRadius: 10,
          border: `1px solid ${COLORS.accentAlpha}`,
        }}>
          <div style={{ fontSize: 24, marginBottom: 6 }}>✅</div>
          <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.accent }}>
            No discrepancies in this period
          </div>
        </div>
      )}
    </div>
  );
}

// ── Tab: Staff Activity ───────────────────────────────────────

function StaffActivityTab({ data: d }) {
  const staff = d.staff || [];

  const chartData = staff.slice(0, 8).map(s => ({
    name:     decodeHtml(s.last_name),
    sections: s.sections_signed,
    meds:     s.meds_documented,
    vitals:   s.vitals_documented,
  }));

  return (
    <div>
      <SectionTitle title="Staff Documentation Activity" subtitle={`${d.date_from} → ${d.date_to} · Supervisors only`} />

      {chartData.length > 0 && (
        <ChartCard title="Documentation by Staff Member" height={260}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={COLORS.border} />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip />
              <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="sections" name="ADL Sections" fill={COLORS.primary} radius={[4, 4, 0, 0]} />
              <Bar dataKey="meds"     name="Meds"         fill={COLORS.accent}  radius={[4, 4, 0, 0]} />
              <Bar dataKey="vitals"   name="Vitals"       fill={COLORS.warning} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      )}

      <div style={{ background: COLORS.surface, borderRadius: 10, border: `1px solid ${COLORS.border}`, overflow: 'hidden' }}>
        <div style={{ padding: '10px 14px', borderBottom: `1px solid ${COLORS.border}`, display: 'grid', gridTemplateColumns: '1fr 60px 60px 60px 60px', gap: 8 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: COLORS.textMuted }}>STAFF</span>
          <span style={{ fontSize: 11, fontWeight: 700, color: COLORS.textMuted, textAlign: 'right' }}>RECORDS</span>
          <span style={{ fontSize: 11, fontWeight: 700, color: COLORS.textMuted, textAlign: 'right' }}>SECTIONS</span>
          <span style={{ fontSize: 11, fontWeight: 700, color: COLORS.textMuted, textAlign: 'right' }}>MEDS</span>
          <span style={{ fontSize: 11, fontWeight: 700, color: COLORS.textMuted, textAlign: 'right' }}>VITALS</span>
        </div>
        {staff.map((s, i) => (
          <div key={i} style={{
            padding: '10px 14px',
            borderBottom: i < staff.length - 1 ? `1px solid ${COLORS.border}` : 'none',
            display: 'grid', gridTemplateColumns: '1fr 60px 60px 60px 60px', gap: 8, alignItems: 'center',
          }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.text }}>{decodeHtml(s.full_name)}</div>
              <div style={{ fontSize: 11, color: COLORS.textMuted }}>{s.role}</div>
            </div>
            <span style={{ fontSize: 12, color: COLORS.primary, textAlign: 'right', fontWeight: 600 }}>{s.records_signed}</span>
            <span style={{ fontSize: 12, color: COLORS.textMuted, textAlign: 'right' }}>{s.sections_signed}</span>
            <span style={{ fontSize: 12, color: COLORS.textMuted, textAlign: 'right' }}>{s.meds_documented}</span>
            <span style={{ fontSize: 12, color: COLORS.textMuted, textAlign: 'right' }}>{s.vitals_documented}</span>
          </div>
        ))}
        {staff.length === 0 && (
          <div style={{ padding: '24px 16px', textAlign: 'center', color: COLORS.textMuted, fontSize: 13 }}>
            No staff activity in this period.
          </div>
        )}
      </div>
    </div>
  );
}

// ── Shared styles ─────────────────────────────────────────────

const dateInputStyle = {
  padding: '5px 8px', borderRadius: 6,
  border: `1px solid ${COLORS.border}`, fontSize: 12,
  color: COLORS.text, background: COLORS.surface,
  fontFamily: 'inherit',
};